import {Action} from "redux-actions";
import {
	JOB_AGENT_UPDATED,
	JOB_CREATED,
	JOB_EVENTS_FETCHED,
	JOB_FETCHED,
	JOB_STATUS_UPDATED,
	JOB_UPDATED,
	JOB_WATCHERS_UPDATED
} from "./actionType";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "redux";
import {api} from "../../../api/api";
import Job from "../../../model/job/Job";
import {UpdateJobAgentApiDto, UpdateJobStatusApiDto} from "../../../api/model/rest";
import Tag from "../../../model/tag/Tag";
import {TAGS_SET_TO_JOB} from "../tag/actionType";
import JobEvent from "../../../model/job/event/JobEvent";
import {Option} from "ts-option";
import JsonConverter from "../../../api/helper/JsonConverter";
import {fetchSite} from "../site/site";
import {fetchIndividualContact} from "../contacts/contacts";
import JobData from "../../../model/job/JobData";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import Toast from "../../../components/helper/Toast";
import {WarrantyDownloadErrors} from "../../../components/job/warrantyInfo/form/edit/EditWarrantyInfoForm";
import {fetchInvoice} from "../invoice/invoice";

/**
 * Job creation successful action.
 * @param {Job} job Created job.
 * @returns {Action<JOB_CREATED>}
 */
export function jobCreated(job: Job): Action<JOB_CREATED> {
	return {type: JOB_CREATED, payload: {job}};
}

/**
 * Job update successful action.
 * @param {Job} job Updated job.
 * @returns {Action<JOB_UPDATED>}
 */
export function jobUpdated(job: Job): Action<JOB_UPDATED> {
	return {type: JOB_UPDATED, payload: {job}};
}

/**
 * Job fetched successful action.
 * @param {Job} job fetched lead.
 * @return {Action<JOB_FETCHED>}
 */
export function jobFetched(job: Job): Action<JOB_FETCHED> {
	return {type: JOB_FETCHED, payload: {job}}
}

/**
 * Job status successful updated action.
 * @param {string} jobId
 * @param {string} statusId new job status id
 * @return {Action<JOB_STATUS_UPDATED>}
 */
export function statusUpdated(jobId: string, statusId: string): Action<JOB_STATUS_UPDATED> {
	return {type: JOB_STATUS_UPDATED, payload: {jobId, statusId}}
}

/**
 * Job tag added successful action
 * @param {string} jobId
 * @param {Tag[]} tags
 * @returns {{type: string | TAGS_SET_TO_JOB; payload: {jobId: string; tags: Tag[]}}}
 */
export function jobTagsSet(jobId: string, tags: Tag[]) {
	return {type: TAGS_SET_TO_JOB, payload: {jobId, tags}}
}

/**
 * Job agent updated successful action.
 * @param {string} jobId
 * @param {Option<string>} agentId
 * @returns Action<JOB_AGENT_UPDATED>
 */
export function jobAgentUpdated(jobId: string, agentId: Option<string>): Action<JOB_AGENT_UPDATED> {
	return {type: JOB_AGENT_UPDATED, payload: {jobId, agentId}};
}

/**
 * Job events fetched successful action.
 * @param {string} jobId
 * @param {JobEvent[]} events
 * @returns {{type: string | JOB_EVENTS_FETCHED; payload: {jobId: string; events: JobEvent[]}}}
 */
export function jobEventsFetched(jobId: string, events: JobEvent[]) {
	return {type: JOB_EVENTS_FETCHED, payload: {jobId, events}}
}

/**
 * Job watchers fetched successful action.
 * @param {string} jobId
 * @param {string[]} watchers
 * @returns Action<JOB_WATCHERS_UPDATED>
 */
export function jobWatchersUpdated(jobId: string, watchers: string[]): Action<JOB_WATCHERS_UPDATED> {
	return {type: JOB_WATCHERS_UPDATED, payload: {jobId, watchers}}
}

/**
 * Create new job
 * @param {JobData} job Job data object.
 * @param {OnSuccessData} onSuccess On success callback.
 * @param {OnFailure} onFailure On failure callback.
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function createJob(job: JobData, onSuccess: OnSuccessData<Job>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.createJob(job).then((newJob: Job) => {
				dispatch(jobCreated(newJob));
				onSuccess(newJob);
			}
		).catch(onFailure)
	}
}

/**
 * Fetch single job and it dependencies.
 * @param {string} jobId job id to fetch.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function fetchJob(jobId: string, onSuccess: OnSuccessData<Job>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.fetchJob(jobId).then(job => {
			job.data.siteId.map(sId => dispatch(fetchSite(sId, () => {}, err => notifyBugsnagAndShowToast(err, "Fetch Site"))));
			dispatch(fetchIndividualContact(job.data.principalContactId, () => {},
				error => notifyBugsnagAndShowToast(error, "Fetch Contact")));
			dispatch(jobFetched(job));
			onSuccess(job);
		}).catch(onFailure);
	}
}

/**
 * Fetch single job
 * @param jobRefNumber
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function fetchJobByRefNumber(jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.fetchJobByRefNumber(jobRefNumber).then(job => {
			job.data.siteId.map(sId => dispatch(fetchSite(sId, () => {}, err => notifyBugsnagAndShowToast(err, "Fetch Site"))));
			dispatch(fetchIndividualContact(job.data.principalContactId, () => {},
				error => notifyBugsnagAndShowToast(error, "Fetch Contact")));
			dispatch(jobFetched(job));
			onSuccess()
		}).catch(onFailure);
	}
}

/**
 * Update job status.
 * @param {string} jobId Job id to be updated.
 * @param {string} statusId New job status id.
 * @param {OnSuccess} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateJobStatus(jobId: string, statusId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	const statusData: UpdateJobStatusApiDto = {statusId};
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.updateStatus(jobId, statusData).then(() => {
			dispatch(statusUpdated(jobId, statusId));
			dispatch(fetchJobEvents(jobId, () => {}, () => {}));
			dispatch(fetchJob(jobId, () => {}, () => {}));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Fetch job events
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function fetchJobEvents(jobId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.fetchEvents(jobId).then((events: JobEvent[]) => {
			dispatch(jobEventsFetched(jobId, events));
			onSuccess();
		}).catch(onFailure)
	}
}

/**
 * Edit job action
 * @param {Job} job
 * @param {OnSuccessData<Job>} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function editJob(job: Job, onSuccess: OnSuccessData<Job>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.updateJob(job).then((updatedJob: Job) => {
			dispatch(jobUpdated(updatedJob));
			dispatch(fetchJobEvents(job.id, () => {}, () => {}));
			onSuccess(updatedJob);
		}).catch(onFailure);
	}
}

/**
 * Send sms to job contact
 * @param {string} jobId
 * @param {string} message
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function sendSmsToJobContact(jobId: string, message: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.sendSmsToJobContact(jobId, message).then(() => {
			dispatch(fetchJob(jobId, () => {}, () => {}));
			dispatch(fetchJobEvents(jobId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Attach uploaded files to job
 * @param {string} jobId
 * @param attachmentIds
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function attachUploadedFilesToJob(jobId: string, attachmentIds: string[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.attachUploadedFiles(jobId, attachmentIds).then(() => {
			dispatch(fetchJobEvents(jobId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Set tags to job
 * @param {string} jobId
 * @param {Tag[]} tags
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function setJobTags(jobId: string, tags: Tag[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.setJobTags(jobId, tags.map(t => t.name)).then(() => {
			dispatch(jobTagsSet(jobId, tags));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Update job agent.
 * @param {string} jobId
 * @param {Option<string>} agentId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function updateJobAgent(jobId: string, agentId: Option<string>, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const data: UpdateJobAgentApiDto = JsonConverter.removeEmptyFields({agentId: agentId.orNull});
		api.instance.jobApi.updateJobAgent(jobId, data).then(() => {
			dispatch(jobAgentUpdated(jobId, agentId));
			dispatch(fetchJob(jobId, () => {}, () => {}));
			dispatch(fetchJobEvents(jobId, () => {}, () => {}));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Update job watchers action.
 * @param {string} jobId job identifier.
 * @param {string[]} watchers
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function updateJobWatchers(jobId: string, watchers: string[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.updateJobWatchers(jobId, watchers).then(() => {
				dispatch(jobWatchersUpdated(jobId, watchers));
				onSuccess();
			}
		).catch(onFailure)
	}
}

export function sendEmailToJobContact(jobId: string,
	subject: string,
	message: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.sendEmailToJobContact(jobId, subject, message).then(() => {
			dispatch(fetchJob(jobId, () => {}, () => {}));
			dispatch(fetchJobEvents(jobId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Add call event
 * @param {string} jobId
 * @param {string} message
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function addCallEvent(jobId: string, message: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.addCallEvent(jobId, message).then(() => {
			dispatch(fetchJobEvents(jobId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Send sms to job creator event
 * @param {string} jobId
 * @param {string} message
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function sendSmsToJobCreator(jobId: string,
	message: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function() {
		api.instance.jobApi.sendSmsToJobCreator(jobId, message).then(onSuccess);
	}
}

export function downloadWarrantyCsv(jobId: string) {
	api.instance.jobApi.downloadWarrantyCsv(jobId).then(
		() => Toast.success("Warranty csv file has been downloaded into GDrive", "Warranty csv download")).catch((error: any) => {
		let err: string;
		if (error && error.response && error.response.status === 409) {
			let missingData: string[] = [];
			let errorMessages: string[] = [];
			error.response.data.errors.map((e: string) => {
				switch (e) {
					case WarrantyDownloadErrors.JobNotCompleted:
						errorMessages.push("Job is not completed.");
						break;
					case WarrantyDownloadErrors.SingleWarrantyCodeRequired:
						errorMessages.push("Single warranty code required.");
						break;
					case WarrantyDownloadErrors.NoWorcesterAssetInJob:
						errorMessages.push("Missing Worcester asset in job");
						break;
					case WarrantyDownloadErrors.MissingBoilerSerialNumber:
						missingData.push("boiler serial number");
						break;
					case WarrantyDownloadErrors.MissingPostCode:
						missingData.push("post code");
						break;
					case WarrantyDownloadErrors.MissingHouseIdentifier:
						missingData.push("house identifier");
						break;
					case WarrantyDownloadErrors.MissingStreet:
						missingData.push("street name");
						break;
					case WarrantyDownloadErrors.MissingCity:
						missingData.push("city name");
						break;
					case WarrantyDownloadErrors.MissingCustomerTitle:
						missingData.push("customer title");
						break;
				}
			});
			Toast.error("Cannot generate warranty file. " + generateFinalErrorMessage(missingData, errorMessages),
				"Warranty csv download",
				{autoClose: false});
		}
		else if (error && error.response && error.response.status === 400) {
			err = "Cannot generate warranty file. Boiler brand is not supported.";
			Toast.error(err, "Warranty csv download");
		}
		else {
			notifyBugsnagAndShowToast(error, "Warranty csv download")
		}
	})
}

const generateFinalErrorMessage = (missingData: string[], errorMessages: string[]) => {
	if (missingData.length == 1) {
		errorMessages.push(`${missingData[0]} is missing.`);
	}
	else if (missingData.length > 1) {
		const lastMissingData = missingData.pop();
		errorMessages.push(`${missingData.join(", ")} and ${lastMissingData} is missing.`);
	}
	return errorMessages.map(em => em.capitalize()).join(" ");
};

/**
 * Unmarked job as paid
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function unmarkedAsPaid(jobId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobApi.unmarkedAsPaid(jobId).then(() => {
			dispatch(fetchJobEvents(jobId, () => {}, onFailure));
			dispatch(fetchJob(jobId, () => {}, onFailure));
			dispatch(fetchInvoice(jobId, () => {}, onFailure));
			onSuccess();
		}).catch(onFailure);
	}
}

